import {
  documentServiceClient,
  documentServicePublicClient,
  getDocumentServicePublicClientConfig,
  getDocumentServiceSchemaClient,
} from 'services/api/client';
import type {
  DocumentImageLibraryApiResponse,
  DocumentSectionsResponse,
  GetContentStyleByContentIdType,
} from '../../documents/DocumentRepository.ts';
import { Endpoints } from '../../../utils/endpoints.ts';
import type { SectionsSignaturesType, GetSignaturesByDocumentIdApiResponse, SignatureBox } from 'services/entities/SignatureTypes';
import type { PromiseAllSettledResult } from '../../../interfaces/PromisedAllSettledResult.ts';
import { transformSignatureBoxApiResponse } from './ApiSignatureDocumentServiceRepository.ts';
import type { FontEntity } from 'services/entities/FontsEntity.ts';

import type {
  CompaniesControllerCreateCompanyMutationRequest,
  JSONCommentDto,
  ContactsControllerCreateContactMutationRequest,
  ContactResponse,
  CompanyResponse,
  AddImageLibraryResult,
  ContentControllerUpdateMutationRequest,
  ContentControllerGetLiveblocksPrivateAuth200,
  ContentResponse,
  ContentControllerFindImageLibraryById200,
  FontsControllerGetFonts200,
  ContentPublicControllerCreateCommentMutationRequest,
  ContentPublicControllerCreateCommentMutationResponse,
  ContentPublicControllerResolveCommentMutationRequest,
  ContentPublicControllerResolveComment200,
  ContentPublicControllerDeleteComment200,
  ContentPublicControllerPdfGenerateMutationRequest,
  GenerateTokenResponseDto,
  ContentDocumentDataResponseDto,
  ProposifyVariablesResponseDto,
} from '@proposify/document-service-api-client';
import { documentServiceClient as documentServiceApiClient, documentServiceSchemas } from '@proposify/document-service-api-client';
import type { LiveblocksPublicAuthBody } from '@proposify/document-service-api-client/dist/types/LiveblocksPublicAuthBody';
import type {
  ApprovalStateResponseDto,
  ApproverUserDto,
  DocumentDto as ShareDocumentResponseDto,
  DocumentsControllerShareDocumentMutationRequest,
  DocumentsControllerUpdateDocumentStatusMutationRequest,
  DocumentsPublicControllerGetDocumentMetadataByContentIdPathParams,
  DocumentsPublicControllerGetDocumentMetadataByDocumentIdPathParams,
  DocumentMetadataResponseDto,
  DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse,
  HydratedApproverResponseDto,
  EmbedControllerValidateEmbedMutationRequest,
} from '@proposify/document-service-api-client';
import {
  documentsGetApprovalState,
  documentsApprove,
  documentsPublicGetDocumentMetadataByContentId,
  documentsPublicGetDocumentMetadataByDocumentId,
  documentsRequestApproval,
  documentsShareDocument,
  documentsUpdateDocumentStatus,
  embedValidateEmbed,
} from '@proposify/document-service-api-client';
import { type EmbedValidationStatus, isEmbedValidationStatus } from 'components/editor/embed/embed';
import { getUserTokenData } from '../../../utils/authentication.ts';
import { ProspectType } from '../interfaces/ProspectRepository.ts';
import { generateProspectUid } from './ApiProspectsRepository.ts';
import type { DocumentProspect } from '../../documents/entities.ts';
import { DocumentStatus, PricingTotalType } from '../../documents/entities.ts';
import { trackError } from '@proposify/frontend-observability';
import { VariableDomain, type VariablesGroup, type VariableNode, crmV1WireNodeSchema } from '../../entities/VariablesEntity';
import { crmV2VariableNodeSchema, type CrmV2VariableNode } from '../../entities/crmVariableNodeV2';
import { appNativeVariablesV2ResponseSchema } from '../../entities/appNativeVariableV2';
import { z } from 'zod';
import type { ContentControllerUpdateMutationResponse } from '@proposify/document-service-api-client/dist/types/ContentControllerUpdate';
import { toCompanyId, toPersonId } from '../../entities/MigrationEntities.ts';

export type GetDocumentById = Omit<
  DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse,
  'total_type' | 'account_id' | 'prospect' | 'is_locked' | 'assigned_user' | 'content_id' | 'created_by'
> & {
  isLocked: DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse['is_locked'];
  assignedUser: DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse['assigned_user'];
  isDocumentBelongsToSameAccount: boolean;
  contentId: DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse['content_id'];
  accountId: string;
  totalType: DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse['total_type'];
  createdBy: DocumentsPublicControllerGetDocumentMetadataByDocumentIdQueryResponse['created_by'];
  documentId?: string;
  prospect: DocumentProspect | null;
  displayDocumentTotal?: boolean;
};

type DocumentMetadataApiResponse = DocumentMetadataResponseDto & {
  document_id?: string;
  display_document_total?: boolean;
};

export type DocumentApprovalState = {
  status: DocumentStatus;
  requiresApproval: boolean;
  approvers: HydratedApproverResponseDto[];
  eligibleApprovers: ApproverUserDto[];
  canApprove: boolean;
};

const approvalStatusToDocumentStatus = {
  DRAFT: DocumentStatus.DRAFT,
  WON: DocumentStatus.WON,
  VIEWED: DocumentStatus.VIEWED,
  LOST: DocumentStatus.LOST,
  SENT: DocumentStatus.SENT,
  AWAITING_SIGNATURE: DocumentStatus.AWAITING_SIGNATURE,
  AWAITING_APPROVAL: DocumentStatus.AWAITING_APPROVAL,
  APPROVED: DocumentStatus.APPROVED,
} satisfies Record<ApprovalStateResponseDto['status'], DocumentStatus>;

type TemplateByIdResponse = {
  id: string;
  name: string;
  type: string;
  subType?: string;
  data: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
};

export const parseDocumentMetadata = ({
  title,
  price,
  status,
  is_locked,
  assigned_user = null,
  prospect: prospectFromApi,
  content_id,
  document_id: documentId,
  account_id,
  total_type,
  created_by: createdBy,
  display_document_total,
}: DocumentMetadataApiResponse): GetDocumentById => {
  let prospect: GetDocumentById['prospect'] = null;

  if (prospectFromApi != null) {
    const base = {
      id: prospectFromApi.id,
      name: prospectFromApi.name,
      uid: generateProspectUid(prospectFromApi),
    };
    if (prospectFromApi.type === ProspectType.COMPANY) {
      const contractId = toCompanyId(prospectFromApi?.contact?.id);
      const contact = contractId
        ? {
            id: contractId,
            name: prospectFromApi?.contact?.name ?? '',
            email: prospectFromApi?.contact?.email ?? '',
          }
        : null;
      prospect = {
        ...base,
        type: ProspectType.COMPANY,
        contact,
      };
    } else {
      const contractId = toPersonId(prospectFromApi?.contact?.id);
      const contact = contractId
        ? {
            id: contractId,
            name: prospectFromApi?.contact?.name ?? '',
            email: prospectFromApi?.contact?.email ?? '',
          }
        : null;
      prospect = {
        ...base,
        type: ProspectType.PERSON,
        contact,
      };
    }
  }

  const userToken = getUserTokenData();
  const loggedUserAccountId = userToken?.getAccountIdIfValid();

  return {
    title,
    price,
    status,
    isLocked: is_locked ?? false,
    assignedUser: assigned_user as GetDocumentById['assignedUser'],
    prospect,
    isDocumentBelongsToSameAccount: account_id === loggedUserAccountId,
    contentId: content_id,
    documentId,
    accountId: String(account_id),
    totalType: total_type ?? PricingTotalType.CALCULATED,
    createdBy,
    displayDocumentTotal: display_document_total,
  };
};

function mapVariableNode(node: VariableNode): VariableNode {
  const { path, variables, ...rest } = node;
  return {
    ...rest,
    path: path.replace(VariableDomain.CRM_VARIABLES, VariableDomain.INTEGRATIONS),
    variables: variables?.map(mapVariableNode) ?? [],
  };
}

const CRM_VARIABLES_V2_PREFIX = '/v2';

const APP_NATIVE_VARIABLES_V2_PATH = (contentId: string) => `/api/variable/v2/${contentId}`;

/**
 * @deprecated Frozen v1 app-native variables parser, live only while `app-native-variables-v2-route`
 * defaults off; delete with the default-on flip (SP-4507 D4 precedent).
 */
function parseAppNativeV1Group(response: ProposifyVariablesResponseDto): VariablesGroup {
  const normalizeValue = (value: unknown): string | number | null =>
    typeof value === 'string' || typeof value === 'number' || value === null ? value : null;

  return {
    client: response.client.map((v) => ({ ...v, value: normalizeValue(v.value) })),
    proposal: response.proposal.map((v) => ({ ...v, value: normalizeValue(v.value) })),
    user: response.user.map((v) => ({ ...v, value: normalizeValue(v.value) })),
  };
}

function parseAppNativeV2Group(response: unknown): VariablesGroup {
  return appNativeVariablesV2ResponseSchema.parse(response);
}

function parseCrmV1Nodes(response: unknown): VariableNode[] {
  return z.array(crmV1WireNodeSchema).parse(response);
}

// The v2 endpoint sends `variables: null` on leaf nodes; normalize it to `[]` so the
// result matches VariableNode's contract (variables is an array or omitted, never null).
function normalizeCrmV2Node(node: CrmV2VariableNode): VariableNode {
  return { ...node, variables: node.variables?.map(normalizeCrmV2Node) ?? [] };
}

function parseCrmV2Nodes(response: unknown): VariableNode[] {
  return z.array(crmV2VariableNodeSchema).parse(response).map(normalizeCrmV2Node);
}

async function fetchCrmVariables(
  url: string,
  endpoint: string,
  parseNodes: (response: unknown) => VariableNode[],
  trackErrorContext?: Record<string, unknown>
): Promise<VariableNode[]> {
  try {
    const response = await documentServiceClient.get<unknown>(url);
    const variableNodes = parseNodes(response);
    return variableNodes.map(mapVariableNode);
  } catch (error) {
    const trackedError = error instanceof Error ? error : new Error(String(error));
    trackError(trackedError, { endpoint, ...trackErrorContext });
    throw error;
  }
}

export const apiDocumentServiceRepository = {
  patchContent: async ({
    contentId,
    payload,
  }: {
    contentId: string;
    payload: ContentControllerUpdateMutationRequest;
  }): Promise<ContentControllerUpdateMutationResponse | void> => {
    try {
      const data = await documentServiceClient.patch<ContentResponse>(`/content/${contentId}`, payload);
      return data;
    } catch (error) {
      trackError(error, {
        message: `Document update failed to write to document service for contentId: ${contentId}`,
        context: {
          endpoint: 'patchContent',
          contentId,
          payload,
        },
      });
    }
  },
  validateEmbed: async (payload: { embedUrl: string }): Promise<{ validationStatus: EmbedValidationStatus }> => {
    const request: EmbedControllerValidateEmbedMutationRequest = {
      embedUrl: payload.embedUrl,
    };
    let response;
    try {
      response = await embedValidateEmbed(request, getDocumentServiceSchemaClient());
    } catch (error) {
      trackError(error, { endpoint: 'validateEmbed', payload });
      throw error;
    }

    if (!isEmbedValidationStatus(response.validationStatus)) {
      const contractError = new Error('validateEmbed response failed contract shape check');
      trackError(contractError, { endpoint: 'validateEmbed', receivedStatus: response.validationStatus });
      throw contractError;
    }

    return { validationStatus: response.validationStatus };
  },
  getSectionsByContentId: async (contentId: string): Promise<DocumentSectionsResponse> => {
    try {
      // TODO: actual response data type from document service (ContentGridResponse) does not match DocumentSectionsResponse, needs transformation
      return await documentServiceClient.get<DocumentSectionsResponse>(`/content/${contentId}/sections`);
    } catch (error) {
      trackError(error, { endpoint: 'getSectionsByContentId' });
      throw error;
    }
  },
  getDocumentMetadataByContentId: async ({ contentId }: DocumentsPublicControllerGetDocumentMetadataByContentIdPathParams) => {
    try {
      return parseDocumentMetadata(await documentsPublicGetDocumentMetadataByContentId(contentId, getDocumentServicePublicClientConfig()));
    } catch (error) {
      trackError(error, { endpoint: 'documentsPublicGetDocumentMetadataByContentId' });
    }
  },
  getDocumentApprovalState: async (contentId: string): Promise<DocumentApprovalState> => {
    try {
      const response = await documentsGetApprovalState(contentId, getDocumentServiceSchemaClient());
      return {
        status: approvalStatusToDocumentStatus[response.status],
        requiresApproval: response.requires_approval,
        approvers: response.approvers,
        eligibleApprovers: response.eligible_approvers,
        canApprove: response.can_approve,
      };
    } catch (error) {
      trackError(error, { endpoint: 'getDocumentApprovalState', contentId });
      throw error;
    }
  },
  requestApproval: async (contentId: string, payload: { comment?: string }): Promise<void> => {
    try {
      await documentsRequestApproval(contentId, payload, getDocumentServiceSchemaClient());
    } catch (error) {
      trackError(error, { endpoint: 'requestApproval', contentId });
      throw error;
    }
  },
  approveDocument: async (contentId: string, payload: { notes?: string }): Promise<void> => {
    try {
      await documentsApprove(contentId, payload, getDocumentServiceSchemaClient());
    } catch (error) {
      trackError(error, { endpoint: 'approveDocument', contentId });
      throw error;
    }
  },
  /**
   * @deprecated Use getDocumentMetadataByContentId (above) instead.
   */
  getDocumentMetadataByDocumentId: async ({ documentId }: DocumentsPublicControllerGetDocumentMetadataByDocumentIdPathParams) => {
    try {
      return parseDocumentMetadata(
        await documentsPublicGetDocumentMetadataByDocumentId(documentId, getDocumentServicePublicClientConfig())
      );
    } catch (error) {
      trackError(error, { endpoint: 'documentsPublicGetDocumentMetadataByDocumentId' });
    }
  },
  getTipTapAiToken: async (): Promise<GenerateTokenResponseDto | undefined> => {
    try {
      return await documentServiceApiClient.editorAiGenerateToken(getDocumentServiceSchemaClient());
    } catch (error) {
      trackError(error, { endpoint: 'getTiptapAiToken' });
    }
  },
  contentPdfExport: async (contentId: string, documentPreviewLink: string) => {
    // TODO: actual response data type from document service (ContentPublicControllerPdfGenerate201) does not match { pdfLink: string }, needs transformation
    return documentServiceClient.post<{ pdfLink: string }>(`/content/${contentId}/pdf_generate`, {
      documentPreviewLink,
    } satisfies ContentPublicControllerPdfGenerateMutationRequest);
  },
  getContentStyleSettings: async (contentId: string) => {
    try {
      // TODO: actual response data type from document service (ContentPublicControllerGetStyleSettings200) does not match GetContentStyleByContentIdType, needs transformation
      return await documentServiceClient.get<GetContentStyleByContentIdType>(`/content/${contentId}/style_settings`);
    } catch (error) {
      trackError(error, { endpoint: 'getContentStyleSettings' });
      throw error;
    }
  },
  getDocumentLibraryImages: async (contentId: string) => {
    try {
      const response = await documentServiceClient.get<ContentControllerFindImageLibraryById200>(
        Endpoints.documentImageLibraryImages(contentId)
      );
      return response satisfies DocumentImageLibraryApiResponse;
    } catch (error) {
      trackError(error, { endpoint: 'getDocumentLibraryImages' });
      throw error;
    }
  },
  uploadDocumentLibraryImages: async ({
    contentId,
    images,
  }: {
    contentId: string;
    images: File[];
  }): Promise<PromiseAllSettledResult<AddImageLibraryResult>[]> => {
    return (await Promise.allSettled(
      Array.from(images).map((image) => {
        const formData = new FormData();
        formData.append('image', image);
        try {
          return documentServiceClient.post<AddImageLibraryResult>(Endpoints.documentUploadImage(contentId), formData);
        } catch (error) {
          trackError(error, { endpoint: 'uploadDocumentLibraryImages', payload: formData });
          throw error;
        }
      })
    )) as PromiseAllSettledResult<AddImageLibraryResult>[];
  },
  getSignaturesByContentId: async (contentId: string) => {
    const sectionsSignatures = await documentServiceClient.get<GetSignaturesByDocumentIdApiResponse>(`/document/${contentId}/signatures`);

    const formattedSectionsSignatures: SectionsSignaturesType = {};
    Object.keys(sectionsSignatures).forEach((sectionId) => {
      formattedSectionsSignatures[sectionId] = sectionsSignatures[sectionId].map((signature): SignatureBox => {
        return transformSignatureBoxApiResponse(signature);
      });
    });

    return formattedSectionsSignatures;
  },
  getInstalledFonts: async (contentId: string) => {
    try {
      const response = await documentServiceClient.get<FontsControllerGetFonts200>(`/documents/${contentId}/fonts`);
      return response satisfies FontEntity[];
    } catch (error) {
      trackError(error, { endpoint: 'getInstalledFonts' });
      return [];
    }
  },
  createContact: async (contactData: ContactsControllerCreateContactMutationRequest) => {
    return await documentServiceClient.post<ContactResponse>(Endpoints.createContact, contactData);
  },
  createCompany: async (companyData: CompaniesControllerCreateCompanyMutationRequest) => {
    return await documentServiceClient.post<CompanyResponse>(Endpoints.createCompany, companyData);
  },
  createComment: async (blockId: string, payload: ContentPublicControllerCreateCommentMutationRequest): Promise<JSONCommentDto | null> => {
    try {
      return await documentServiceClient.post<ContentPublicControllerCreateCommentMutationResponse>(`/content/${blockId}/comment`, payload);
    } catch (error) {
      trackError(error, { endpoint: 'createComment' });
      throw error;
    }
  },
  updateComment: async (blockId: string, payload: ContentPublicControllerResolveCommentMutationRequest): Promise<JSONCommentDto | null> => {
    try {
      return await documentServiceClient.patch<ContentPublicControllerResolveComment200>(
        `/content/${blockId}/comment/${payload.threadId}/resolved`,
        payload
      );
    } catch (error) {
      trackError(error, { endpoint: 'updateComment' });
      throw error;
    }
  },
  deleteComment: async (blockId: string, threadId: string): Promise<JSONCommentDto | null> => {
    try {
      return await documentServiceClient.delete<ContentPublicControllerDeleteComment200>(`/content/${blockId}/comment/${threadId}`);
    } catch (error) {
      trackError(error, { endpoint: 'deleteComment' });
      throw error;
    }
  },
  copyTemplateAsDocument: async ({
    templateId,
    name,
  }: {
    templateId: string;
    name?: string;
  }): Promise<{
    contentId: string;
    documentId: string;
  }> => {
    const documentServiceSchemaClientConfig = getDocumentServiceSchemaClient();
    const response = await documentServiceApiClient.contentCopy(
      templateId,
      {
        asDocument: true,
        copyFromSharedTemplateAccount: false,
      },
      {
        name: name,
      },
      documentServiceSchemaClientConfig
    );

    const parsedResponse = documentServiceSchemas.contentCopyMutationResponseSchema.safeParse(response);

    if (!parsedResponse.success) {
      trackError(new Error(`Copy template as document response schema validation failed: ${parsedResponse.error.message}`), {
        context: {
          errorFrom: 'frontend',
          cause: 'document-service contentCopy response schema validation failed',
        },
      });
    }

    return {
      contentId: response.id,
      documentId: (response.data as Record<string, unknown>)?.documentId as string,
    };
  },
  copyTemplateFromTemplateGallery: async ({ templateId }: { templateId: string }): Promise<{ contentId: string; assetId: string }> => {
    const documentServiceSchemaClientConfig = getDocumentServiceSchemaClient();
    const response = await documentServiceApiClient.contentCopy(
      templateId,
      {
        asDocument: false,
        copyFromSharedTemplateAccount: true,
      },
      {
        name: 'Untitled Template',
      },
      documentServiceSchemaClientConfig
    );

    const parsedResponse = documentServiceSchemas.contentCopyMutationResponseSchema.safeParse(response);

    if (!parsedResponse.success) {
      trackError(new Error(`Copy template from template gallery response schema validation failed: ${parsedResponse.error.message}`), {
        context: {
          errorFrom: 'frontend',
          cause: 'document-service contentCopy response schema validation failed',
        },
      });
    }

    return {
      contentId: response.id,
      assetId: (response.data as Record<string, unknown>)?.assetId as string,
    };
  },
  copyDocumentAsTemplate: async ({
    contentId,
    templateName,
  }: {
    contentId: string;
    templateName: string;
  }): Promise<{ contentId: string; legacyAssetId: string }> => {
    const response = await documentServiceApiClient.documentsCreateTemplateFromDocument(
      contentId,
      { templateName },
      getDocumentServiceSchemaClient()
    );

    const parsedResponse = documentServiceSchemas.documentsCreateTemplateFromDocumentMutationResponseSchema.safeParse(response);

    if (!parsedResponse.success) {
      trackError(new Error(`Copy document as template response schema validation failed: ${parsedResponse.error.message}`), {
        context: {
          errorFrom: 'frontend',
          cause: 'document-service documentsCreateTemplateFromDocument response schema validation failed',
        },
      });
    }

    return response;
  },
  authLiveblocksPrivate: async (contentId: string): Promise<ContentControllerGetLiveblocksPrivateAuth200> => {
    try {
      return await documentServiceClient.post<ContentControllerGetLiveblocksPrivateAuth200>(
        `/content/${contentId}/liveblocks_auth/private`,
        null
      );
    } catch (error) {
      trackError(error, { endpoint: 'authLiveblocksPrivate' });
      throw error;
    }
  },
  authLiveblocksPublic: async (
    contentId: string,
    payload: LiveblocksPublicAuthBody
  ): Promise<ContentControllerGetLiveblocksPrivateAuth200> => {
    try {
      return await documentServiceClient.post(`/content/${contentId}/liveblocks_auth/public`, payload);
    } catch (error) {
      trackError(error, { endpoint: 'authLiveblocksPublic' });
      throw error;
    }
  },
  updateDocumentStatus: async (contentId: string, payload: DocumentsControllerUpdateDocumentStatusMutationRequest) => {
    try {
      return await documentsUpdateDocumentStatus(contentId, payload, getDocumentServiceSchemaClient());
    } catch (error) {
      trackError(error, {
        message: `Document status update failed to write to document service for contentId: ${contentId}`,
        context: {
          endpoint: 'updateDocumentStatus',
          contentId,
          payload,
        },
      });
      throw error;
    }
  },
  shareDocument: async ({
    id,
    payload,
  }: {
    id: string;
    payload: DocumentsControllerShareDocumentMutationRequest;
  }): Promise<ShareDocumentResponseDto> => {
    try {
      return await documentsShareDocument(id, payload, getDocumentServiceSchemaClient());
    } catch (error) {
      trackError(error, {
        context: {
          message: `Document share failed to write to document service`,
          endpoint: 'shareDocument',
          errorFrom: 'frontend',
        },
      });
      throw error;
    }
  },
  getVariables: async (contentId: string, v2RouteEnabled = false): Promise<VariablesGroup> => {
    try {
      if (v2RouteEnabled) {
        const response = await documentServicePublicClient.get<unknown>(APP_NATIVE_VARIABLES_V2_PATH(contentId));
        return parseAppNativeV2Group(response);
      }

      const variables = await documentServiceApiClient.variablesPublicGetContentVariables(
        contentId,
        getDocumentServicePublicClientConfig()
      );
      return parseAppNativeV1Group(variables);
    } catch (error) {
      trackError(error, { endpoint: 'getVariables' });
      throw error;
    }
  },
  getDocumentData: async ({ contentId }: { contentId: string }): Promise<ContentDocumentDataResponseDto> => {
    try {
      return await documentServiceApiClient.contentPublicGetDocumentData(contentId, {
        ...getDocumentServicePublicClientConfig(),
      });
    } catch (error) {
      trackError(error, { endpoint: 'getDocumentData' });
      throw error;
    }
  },
  getCRMVariables: async (contentId: string, v2RouteEnabled = false): Promise<VariableNode[]> => {
    const url = v2RouteEnabled ? `/document${CRM_VARIABLES_V2_PREFIX}/${contentId}/crm-variables` : `/document/${contentId}/crm-variables`;
    return fetchCrmVariables(url, 'getCRMVariables', v2RouteEnabled ? parseCrmV2Nodes : parseCrmV1Nodes);
  },
  getContentByAssetId: async (assetId: string): Promise<ContentResponse> => {
    try {
      return await documentServiceApiClient.contentFindContentByAssetId(assetId, getDocumentServiceSchemaClient());
    } catch (error) {
      trackError(error, { endpoint: 'getContentByAssetId' });
      throw error;
    }
  },
  getTemplateById: async (id: string): Promise<TemplateByIdResponse> => {
    try {
      return await documentServiceClient.get<TemplateByIdResponse>(`/template/${id}`);
    } catch (error) {
      trackError(error, { endpoint: 'getTemplateById' });
      throw error;
    }
  },
  getTemplateCRMVariables: async (contentId: string, v2RouteEnabled = false): Promise<VariableNode[]> => {
    const url = v2RouteEnabled ? `/template${CRM_VARIABLES_V2_PREFIX}/${contentId}/crm-variables` : `/template/${contentId}/crm-variables`;
    return fetchCrmVariables(url, 'getTemplateCRMVariables', v2RouteEnabled ? parseCrmV2Nodes : parseCrmV1Nodes, {
      errorFrom: 'frontend',
      payload: { contentId },
    });
  },
  documentViewed: async ({
    contentId,
    viewerId,
    viewerEmail,
  }: {
    contentId: string;
    viewerId: string;
    viewerEmail?: string;
  }): Promise<void> => {
    try {
      await documentServiceApiClient.documentsPublicDocumentViewed(
        contentId,
        {
          viewerId,
          viewerEmail,
        },
        getDocumentServiceSchemaClient()
      );
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)), {
        endpoint: 'documentViewed',
        errorFrom: 'frontend',
        payload: {
          contentId,
          viewerId,
          viewerEmail,
        },
      });
    }
  },
};
